import { axios, getToken, apiUrl } from "../../settings";

type DataServiceResponse = {
  data?: any;
  error?: boolean;
  success?: boolean;
  message?: string;
  type?: string;
};

export type LoginUserWithTwoFactor = {
  user_id: number;
  otp: string;
  two_factor_user_token?: string;
};

export const ClearAllRedisDataAction = (): Promise<DataServiceResponse> =>
  axios
    .get(`${apiUrl}/clear_all_redis_dictionaries`, getToken())
    .then(({ data }) => data);

export const GetAllCandleDataAction = (): Promise<DataServiceResponse> =>
  axios.get(`${apiUrl}/getCandelData`, getToken()).then(({ data }) => data);

export const WeeklyEmailAllUsersAction = (): Promise<DataServiceResponse> =>
  axios
    .post(
      `${apiUrl}/admin/send_weekly_metrics_update_notification`,
      {},
      getToken()
    )
    .then(({ data }) => data);

export const WeeklyEmailSelectedUsersAction = (
  payload: any
): Promise<DataServiceResponse> =>
  axios
    .post(
      `${apiUrl}/admin/send_weekly_metrics_update_notification`,
      {
        send_to_given_emails: [],
        send_to_given_user_ids: payload,
        update_weekly_notification_for_given_users: true,
      },
      getToken()
    )
    .then(({ data }) => data);

export const UpdateCalculationUsersAction = (
  payload: any
): Promise<DataServiceResponse> =>
  axios
    .post(`${apiUrl}/admin/v1/calculations/user/update`, payload, getToken())
    .then(({ data }) => data);

export const AddSplitForInstrumentAction = (
  payload: any
): Promise<DataServiceResponse> =>
  axios
    .post(
      `${apiUrl}/admin/v1/instrument_split/manual_feed`,
      payload,
      getToken()
    )
    .then(({ data }) => data);

export const GetSplitForInstrumentAction = (): Promise<DataServiceResponse> =>
  axios
    .get(`${apiUrl}/admin/v1/instrument_split/all`, getToken())
    .then(({ data }) => data);

export const DeleteSplitForInstrumentAction = (
  id: number
): Promise<DataServiceResponse> =>
  axios
    .delete(`${apiUrl}/admin/v1/instrument_split/${id}`, getToken())
    .then(({ data }) => data);

type EnableUserForWeeklyEmailProps = {
  user_id: number;
};

export const EnableUserForWeeklyEmailAction = (
  param: EnableUserForWeeklyEmailProps
): Promise<DataServiceResponse> =>
  axios
    .post(
      `${apiUrl}/admin/make_user_eligible_for_weekly_update_notification`,
      param,
      getToken()
    )
    .then(({ data }) => data);

export const UpdateLatestLoginTimeToYearsBackAction = (
  param: EnableUserForWeeklyEmailProps
): Promise<DataServiceResponse> =>
  axios
    .post(
      `${apiUrl}/admin/update_latest_login_time_to_years_back`,
      param,
      getToken()
    )
    .then(({ data }) => data);

type ResetUserNotificationProps = {
  user_id: number;
};

export const ResetUserNotificationAction = (
  param: ResetUserNotificationProps
): Promise<DataServiceResponse> =>
  axios
    .post(`${apiUrl}/admin/reset_user_notification_status`, param, getToken())
    .then(({ data }) => data);

export const SynchPlaidDataAction = (): Promise<DataServiceResponse> =>
  axios
    .post(
      `${apiUrl}/plaid_admins/synchronize_portfolio_admin`,
      { user_ids: [] },
      getToken()
    )
    .then(({ data }) => data);

export const SynchVezgoDataAction = (): Promise<DataServiceResponse> =>
  axios
    .post(
      `${apiUrl}/vezgo/admin/synchronize_portfolios_admin`,
      { user_ids: [] },
      getToken()
    )
    .then(({ data }) => data);

export const SynchFlanksDataAction = (): Promise<DataServiceResponse> =>
  axios
    .post(
      `${apiUrl}/flanks/admin/synchronize_portfolios_admin`,
      { user_ids: [] },
      getToken()
    )
    .then(({ data }) => data);

type GetSynchConnectionProps = {
  user_id: number;
  third_party_user_connection_id: number;
};

export const SynchConnectionsByIdAction = (
  param: GetSynchConnectionProps
): Promise<DataServiceResponse> =>
  axios
    .post(
      `${apiUrl}/third_party_user_connection_admins/synchronize_connection`,
      param,
      getToken()
    )
    .then(({ data }) => data);

export const ReSyncGivenConnectionAction = (
  param: GetSynchConnectionProps
): Promise<DataServiceResponse> =>
  axios
    .post(
      `${apiUrl}/third_party_user_connection_admins/resynchronize_connection`,
      param,
      getToken()
    )
    .then(({ data }) => data);

export const ReSyncGivenConnectionCompletelyAction = (
  param: GetSynchConnectionProps
): Promise<DataServiceResponse> =>
  axios
    .post(
      `${apiUrl}/third_party_user_connection_admins/resynchronize_connection_completely`,
      param,
      getToken()
    )
    .then(({ data }) => data);

type GetUserWrongTransactionProps = {
  transaction_threshold: number;
};

export const GetUserWrongTransactionAction = (
  param: GetUserWrongTransactionProps
): Promise<DataServiceResponse> =>
  axios
    .post(
      `${apiUrl}/get_users_transactions_more_than_threshold`,
      param,
      getToken()
    )
    .then(({ data }) => data);

export const GetAllReferrerInfoAction = (): Promise<DataServiceResponse> =>
  axios
    .get(`${apiUrl}/get_all_referred_user_card_info`, getToken())
    .then(({ data }) => data);

export const ThirdPartyUserConnectionInfo = (): Promise<DataServiceResponse> =>
  axios
    .get(`${apiUrl}/get_all_third_party_user_connection`, {
      ...getToken(),
      responseType: "blob",
    })
    .then((data) => data);

export const DemoUserTrackingInfoAction = (): Promise<DataServiceResponse> =>
  axios
    .get(`${apiUrl}/admin/insights/get_demo_user_metadata_file`, {
      ...getToken(),
      responseType: "blob",
    })
    .then((data) => data);

export const UsersDashbaordAccessDateAction =
  (): Promise<DataServiceResponse> =>
    axios
      .get(`${apiUrl}/get_all_users_dashboard_access_date`, getToken())
      .then(({ data }) => data);

export const GetNetWorthCalculationAction = (): Promise<DataServiceResponse> =>
  axios
    .get(
      `${apiUrl}/admins/v1/calculations_debug/all_user_net_worth_calculation`,
      getToken()
    )
    .then(({ data }) => data);

type CheckTickerAvailableOnEodProps = {
  symbol: string;
  isin: string;
};

export const CheckTickerAvailableOnEod = (
  payload: CheckTickerAvailableOnEodProps
): Promise<DataServiceResponse> =>
  axios
    .post(`${apiUrl}/check_ticker_available_on_eod`, payload, getToken())
    .then(({ data }) => data);

export const SendReminderMailInactiveUserAction = (
  payload: any
): Promise<DataServiceResponse> =>
  axios
    .post(
      `${apiUrl}/admin/send_delete_connection_reminder_mail_to_inactive_free_users`,
      payload,
      getToken()
    )
    .then(({ data }) => data);

export const SendReminderMailEligibleUserAction = (
  payload: any
): Promise<DataServiceResponse> =>
  axios
    .post(
      `${apiUrl}/admin/make_reminder_mail_users_eligible_for_delete_connections`,
      payload,
      getToken()
    )
    .then(({ data }) => data);

export const DeleteExternalServiceConnectionsUsersAction = (
  payload: any
): Promise<DataServiceResponse> =>
  axios
    .post(
      `${apiUrl}/admin/delete_external_service_connections_for_inactive_free_users`,
      payload,
      getToken()
    )
    .then(({ data }) => data);

export const ModifyEmailByEmail = (
  payload: any
): Promise<DataServiceResponse> =>
  axios
    .post(`${apiUrl}/user/admin_modify_user_email`, payload, getToken())
    .then(({ data }) => data);

export const ModifyEmailByUserId = (
  payload: any
): Promise<DataServiceResponse> =>
  axios
    .post(
      `${apiUrl}/user/admin_modify_user_email_by_user_id`,
      payload,
      getToken()
    )
    .then(({ data }) => data);

export const ConvertConnectionMapping = (
  payload: any
): Promise<DataServiceResponse> =>
  axios
    .post(
      `${apiUrl}/third_party_user_connection_admins/convert_default_account_mapping`,
      payload,
      getToken()
    )
    .then(({ data }) => data);

export const GetMaintenanceModeAction = (): Promise<DataServiceResponse> =>
  axios.get(`${apiUrl}/v1/maintenance`).then(({ data }) => data);

export const ModifyMaintenanceModeAction = (
  payload: any
): Promise<DataServiceResponse> =>
  axios
    .post(`${apiUrl}/v1/maintenance/modify`, payload, getToken())
    .then(({ data }) => data);

type RemovePlaidWebhooksActionProps = {
  limit: number;
};

export const RemovePlaidWebhooksAction = (
  param: RemovePlaidWebhooksActionProps
): Promise<DataServiceResponse> =>
  axios
    .post(
      `${apiUrl}/plaid_admins/remove_plaid_webhook_requests_older_than_limit`,
      param,
      getToken()
    )
    .then(({ data }) => data);

type DownloadCalculationExcelActionProps = {
  user_id: number;
  asset_id?: number | null;
  asset_group_id?: number | null;
  asset_class?: string | null;
  asset_type?: string | null;
  asset_level?: string | null;
};

export const DownloadCalculationExcelAction = (
  param: DownloadCalculationExcelActionProps
): Promise<DataServiceResponse> =>
  axios
    .post(`${apiUrl}/admin/v1/debug/calculations/any`, param, {
      ...getToken(),
      responseType: "blob",
    })
    .then((data) => data);

// Two Factor Authentication
export const LoginUserWithTwoFactorAction = (
  params: LoginUserWithTwoFactor
): Promise<DataServiceResponse> =>
  axios
    .post(`${apiUrl}/login_user_with_two_fa`, params, {
      headers: {
        Authorization: `Bearer ${params?.two_factor_user_token}`,
      },
    })
    .then(({ data }) => data);

type DownloadSessionDataCsvActionProps = {
  session_id: string;
};
export const DownloadSessionDataCsvAction = (
  params: DownloadSessionDataCsvActionProps
): Promise<DataServiceResponse> =>
  axios
    .post(`${apiUrl}/mock_external_service/download_session_data_csv`, params, {
      ...getToken(),
      responseType: "blob",
    })
    .then((data) => data);

type UploadSessionDataCsvActionProps = {
  formData: FormData;
};
export const UplaodSessionDataCsvAction = (
  params: UploadSessionDataCsvActionProps
): Promise<DataServiceResponse> =>
  axios
    .post(`${apiUrl}/mock_external_service/upload_session_data_csv`, params, {
      headers: {
        Accept: "application/json",
        "content-type": "multipart/form-data",
        ...getToken()?.headers,
      },
    })
    .then((data) => data);

export const MockExternalServiceAction = (
  payload: any
): Promise<DataServiceResponse> =>
  axios
    .post(
      `${apiUrl}//mock_external_service/mock_external_service_data_to_given_user`,
      payload,
      getToken()
    )
    .then(({ data }) => data);

type DownloadSessionidsCsvActionProps = {
  connection_id: string;
};
export const DownloadSessionidsCsvAction = (
  params: DownloadSessionidsCsvActionProps
): Promise<DataServiceResponse> =>
  axios
    .post(
      `${apiUrl}/mock_external_service/get_all_session_ids_for_given_connection_id`,
      params,
      {
        ...getToken(),
        responseType: "blob",
      }
    )
    .then((data) => data);

export const ReportErrorsToInstitutionsAction = (
  payload: any
): Promise<DataServiceResponse> =>
  axios
    .post(
      `${apiUrl}/flanks/admin/report_errors_to_flanks_for_institutions`,
      payload,
      getToken()
    )
    .then(({ data }) => data);

type DownloadSessionDataJsonActionProps = {
  session_id: string;
};
export const DownloadSessionDataJsonAction = (
  params: DownloadSessionDataJsonActionProps
): Promise<DataServiceResponse> =>
  axios
    .post(
      `${apiUrl}/mock_external_service/download_session_data_json`,
      params,
      {
        ...getToken(),
        responseType: "blob",
      }
    )
    .then((data) => data);
